import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { ParticipantFormType } from "@shared";
import { participantValidationSchema, toNullable } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CreateProceedingParticipantRequest, proceedingParticipantsApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useParticipantCreation(onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();

  const createParticipantMutation = useMutation({
    mutationFn: (request: CreateProceedingParticipantRequest) =>
      proceedingParticipantsApi.createProceedingParticipant({
        createProceedingParticipantRequest: request,
      }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ProceedingParticipants] });
      showSuccess(t("proceedingParticipants.create.successfullyCreated"));
      onClose();
    },
  });

  const validationSchema = useMemo(() => participantValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<ParticipantFormType>({
    initialValues: {
      type: null,
      name: "",
      street: "",
      houseNumber: "",
      country: "",
      zipCode: "",
      city: "",
      note: null,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      if (values.type == null) {
        return;
      }

      const request: CreateProceedingParticipantRequest = {
        type: values.type,
        name: values.name.trim(),
        street: toNullable(values.street),
        houseNumber: toNullable(values.houseNumber),
        country: toNullable(values.country),
        zipCode: toNullable(values.zipCode),
        city: toNullable(values.city),
        note: toNullable(values.note),
      };

      await createParticipantMutation.mutateAsync(request);
    },
  });

  return { formik };
}
